/**
 * CATÁLOGO DE MÓDULOS — fuente ÚNICA de verdad.
 *
 * Antes esta información vivía dispersa y divergente en 3 sitios: la union
 * `FeatureName`, `MODULES_INFO` (ModulesPage) y `FEATURE_LABELS`
 * (FeatureOverridesCard) — con datos que ni coincidían (p.ej. `ecommerce` figuraba
 * como plan 'pro' en un lado y estaba en 'basic' en PLAN_FEATURES). Ahora todo
 * módulo se define UNA vez aquí, y esos otros se derivan de este catálogo.
 *
 * Hay DOS ejes de visibilidad, independientes (así lo hacen Shopify/Stripe/Vercel):
 *
 *   1) MADUREZ (este archivo, `status`): ¿el módulo está terminado?
 *        development → sin terminar. Oculto a clientes; visible solo en cuentas
 *                      internas (tenants.is_internal).
 *        beta        → usable pero no anunciado. Igual que development de cara al
 *                      cliente por ahora (oculto salvo cuentas internas).
 *        stable      → listo para clientes.
 *
 *   2) ENTITLEMENT (PLAN_FEATURES, features.config.ts): ¿el plan del tenant lo
 *      incluye comercialmente?
 *
 * Un cliente ve un módulo SOLO si es `stable` Y su plan lo incluye. Las cuentas
 * internas ven todo (incluye development/beta). El filtro de madurez se aplica en
 * FeatureFlagsContext, después de resolver el plan + overrides.
 */

import type { FeatureName } from './features.config';

export type ModuleStatus = 'development' | 'beta' | 'stable';

export type ModuleCategory =
  | 'ventas'
  | 'operaciones'
  | 'clientes'
  | 'canales'
  | 'marketing'
  | 'finanzas'
  | 'ia';

export interface ModuleDef {
  key: FeatureName;
  /** Nombre visible (el que ve el usuario en menú, ajustes, catálogo). */
  name: string;
  description: string;
  category: ModuleCategory;
  /** Madurez. Solo `stable` es visible para clientes; el resto solo en cuentas internas. */
  status: ModuleStatus;
}

/**
 * El catálogo. `status` refleja la realidad de desarrollo declarada por el founder
 * (2026-07-13): lo no terminado va `development` y se oculta a clientes, pero sigue
 * visible en las cuentas internas (Sandbox, Dulti, Vitalex) para poder trabajarlo.
 */
export const MODULE_CATALOG: Record<FeatureName, ModuleDef> = {
  // ── Ventas ────────────────────────────────────────────────────────────────
  pos: {
    key: 'pos',
    name: 'Punto de venta',
    description: 'POS para tomar pedidos en tienda física',
    category: 'ventas',
    status: 'development',
  },
  kds: {
    key: 'kds',
    name: 'KDS',
    description: 'Pantalla de cocina (Kitchen Display System)',
    category: 'ventas',
    status: 'development',
  },
  ecommerce: {
    key: 'ecommerce',
    name: 'Tienda online',
    description: 'E-commerce con catálogo, checkout y dominios',
    category: 'ventas',
    status: 'development',
  },
  cod: {
    key: 'cod',
    name: 'Formulario COD',
    description: 'Pago contra entrega con form builder y prevención de fraude',
    category: 'ventas',
    status: 'stable',
  },
  discounts: {
    key: 'discounts',
    name: 'Descuentos',
    description: 'Cupones y códigos de descuento',
    category: 'ventas',
    status: 'development',
  },
  // ── Operaciones ───────────────────────────────────────────────────────────
  operaciones: {
    key: 'operaciones',
    name: 'Operaciones',
    description: 'Ventas, logística y cobranzas unificadas',
    category: 'operaciones',
    status: 'stable',
  },
  logistics: {
    key: 'logistics',
    name: 'Logística propia',
    description: 'Agencias, motorizados y delivery interno',
    category: 'operaciones',
    status: 'stable',
  },
  inventory: {
    key: 'inventory',
    name: 'Inventario avanzado',
    description: 'Control de stock por sucursal y multi-almacén',
    category: 'operaciones',
    status: 'stable',
  },
  // ── Clientes ──────────────────────────────────────────────────────────────
  crm: {
    key: 'crm',
    name: 'CRM y Llamadas',
    description: 'Contactos, pipeline, marcador, voz IA y coaching de llamadas',
    category: 'clientes',
    status: 'stable',
  },
  loyalty: {
    key: 'loyalty',
    name: 'Fidelización',
    description: 'Programa de puntos, recompensas y wallet digital',
    category: 'clientes',
    status: 'stable',
  },
  // ── Canales ───────────────────────────────────────────────────────────────
  whatsapp: {
    key: 'whatsapp',
    name: 'Chats',
    description: 'Bandeja compartida de WhatsApp con equipos, menciones y respuestas rápidas',
    category: 'canales',
    status: 'stable',
  },
  chatbot: {
    key: 'chatbot',
    name: 'Chatbot IA',
    description: 'Constructor de flujos conversacionales con IA y widget de chat',
    category: 'canales',
    status: 'stable',
  },
  agents: {
    key: 'agents',
    name: 'Agentes IA',
    description: 'Studio para crear agentes y flujos conversacionales',
    category: 'canales',
    status: 'development',
  },
  copilot: {
    key: 'copilot',
    name: 'Copiloto',
    description: 'Agente operativo IA: consulta y ejecuta acciones en todo el negocio',
    category: 'canales',
    status: 'development',
  },
  // ── Marketing ─────────────────────────────────────────────────────────────
  marketing: {
    key: 'marketing',
    name: 'Marketing',
    description: 'Campañas y upselling automatizado',
    category: 'marketing',
    status: 'development',
  },
  ads: {
    key: 'ads',
    name: 'Smarty Ads',
    description: 'Campañas de Meta Ads gestionadas con IA',
    category: 'marketing',
    status: 'development',
  },
  // ── Finanzas ──────────────────────────────────────────────────────────────
  finance: {
    key: 'finance',
    name: 'Control de Caja',
    description: 'Gestión financiera y reportes contables',
    category: 'finanzas',
    status: 'stable',
  },
  accounting: {
    key: 'accounting',
    name: 'Contabilidad',
    description: 'Comprobantes electrónicos SUNAT (CPE) y reportes contables',
    category: 'finanzas',
    status: 'development',
  },
  // ── IA ────────────────────────────────────────────────────────────────────
  commander: {
    key: 'commander',
    name: 'IA & Copilot',
    description: 'Asistente IA y chatbots conversacionales',
    category: 'ia',
    status: 'stable',
  },
};

/** Nombre visible de un módulo (reemplaza los labels duplicados). */
export function moduleName(key: FeatureName): string {
  return MODULE_CATALOG[key]?.name ?? key;
}

/** ¿El módulo está listo para clientes (stable)? Los no-stable solo se ven en cuentas internas. */
export function isModuleStable(key: FeatureName): boolean {
  return MODULE_CATALOG[key]?.status === 'stable';
}

/** Claves cuyo módulo NO es stable (development/beta) — las que el filtro de madurez oculta a no-internos. */
export function nonStableModuleKeys(): FeatureName[] {
  return (Object.keys(MODULE_CATALOG) as FeatureName[]).filter((k) => !isModuleStable(k));
}

/**
 * Status efectivo de cada módulo = override de BD (platform_module_status) si existe,
 * sino el default del catálogo (código). Devuelve las claves cuyo status efectivo NO es
 * stable — las que el filtro de madurez oculta. Overrides con claves desconocidas o
 * status inválido se ignoran (cae al default del catálogo).
 */
export function resolveNonStableKeys(statusOverrides: Record<string, ModuleStatus>): FeatureName[] {
  return (Object.keys(MODULE_CATALOG) as FeatureName[]).filter((k) => {
    const status = statusOverrides[k] ?? MODULE_CATALOG[k].status;
    return status !== 'stable';
  });
}

/**
 * Eje de MADUREZ. Toma un mapa de features ya resuelto (plan + tenant_features +
 * overrides) y, si la cuenta NO es interna, apaga toda feature cuyo módulo no sea
 * `stable`. Las cuentas internas (is_internal) ven todo. No muta la entrada.
 *
 * `nonStableKeys` permite pasar el status EFECTIVO (override de BD vía
 * resolveNonStableKeys); por defecto usa el status del catálogo (código). Es la última
 * capa: gana para OCULTAR (nunca reenciende algo ya apagado). Vive aparte para testear.
 */
export function applyMaturityFilter(
  features: Record<string, boolean>,
  isInternal: boolean,
  nonStableKeys: FeatureName[] = nonStableModuleKeys(),
): Record<string, boolean> {
  const out = { ...features };
  if (isInternal) return out;
  for (const key of nonStableKeys) out[key] = false;
  return out;
}
